import { createSlice } from '@reduxjs/toolkit';
import { IStaffUI } from 'types/staff';
import actions from '../actions/shop';
import { ISalonInfoResData, IScheduleBooking } from 'services/types/shop';
import { ICategoryUI } from 'types/category';
import { IServiceUI } from 'types/service';
import { ICustomerUI } from 'types/customer';
import { ISchedulerSalonResData, IStaffResItem } from 'services/types/response';
import { IBlockHourHistoriesResponse } from 'services/types/blockHours';
import set from 'lodash/set';
import { STAFF_CALENDAR_PAGE_SIZE } from 'constants/calendar';

const _scheduleBooking = {
  ['Sun']: {
    enable: true,
    endTime: '21:00',
    startTime: '08:00',
  },
  ['Sat']: {
    enable: true,
    endTime: '21:00',
    startTime: '08:00',
  },
  ['Fri']: {
    enable: true,
    endTime: '21:00',
    startTime: '08:00',
  },
  ['Thu']: {
    enable: true,
    endTime: '21:00',
    startTime: '08:00',
  },
  ['Wed']: {
    enable: true,
    endTime: '21:00',
    startTime: '08:00',
  },
  ['Tue']: {
    enable: true,
    endTime: '21:00',
    startTime: '08:00',
  },
  ['Mon']: {
    enable: true,
    endTime: '21:00',
    startTime: '08:00',
  },
};

const NAME_REDUCER = 'shop';

export type IStateShop = {
  staffs: IStaffUI[],
  pureStaffs: IStaffUI[],
  staffsObject: Record<string, IStaffUI>,
  staffsAvailable: IStaffUI[],
  blockHourHistories: IBlockHourHistoriesResponse,
  scheduleBooking: IScheduleBooking | null,
  salonWorking: ISchedulerSalonResData | null,
  categories: ICategoryUI[],
  allServices: IServiceUI[],
  customers: ICustomerUI[],
  setting: {
    verifyPinChangeAppointment: boolean;
    enableCallerV2: boolean;
  },
  salonInfo: ISalonInfoResData | null;
  clockInStaffs: IStaffResItem[];
  enableCalendarPaging: boolean;
}
const initialState: IStateShop = {
  staffs: [],
  pureStaffs: [],
  staffsObject: {},
  staffsAvailable: [],
  blockHourHistories: { data: [], page: 1, pageSize: 20, total: 0 },
  scheduleBooking: _scheduleBooking,
  salonWorking: null,
  categories: [],
  allServices: [],
  customers: [],
  setting: {
    verifyPinChangeAppointment: false,
    enableCallerV2: false,
  },
  salonInfo: null,
  clockInStaffs: [],
  enableCalendarPaging: false,
};

export const Slice = createSlice({
  name: NAME_REDUCER,
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(actions.clockInStaffs.fetchPaging, (state) => {
        state.clockInStaffs = [];
      })
      .addCase(actions.clockInStaffs.success, (state, { payload }) => {
        state.clockInStaffs = payload;
      })
      .addCase(actions.salonInfo.success, (state, { payload }) => {
        state.salonInfo = payload;
      })
      .addCase(actions.shopAllSetting.success, (state, { payload }) => {
        // Backend value is authoritative on every load — re-derive so a persisted local value can't shadow it.
        state.setting.verifyPinChangeAppointment = !!payload.verifyPinChangeAppointment;
        state.setting.enableCallerV2 = !!payload.enableCallerV2;
      })
      .addCase(actions.pureStaffs.success, (state, { payload }) => {
        state.pureStaffs = payload;
      })
      .addCase(actions.scheduler.success, (state, { payload }) => {
        state.salonWorking = payload;
        const _scheduleBooking = {
          ['Sun']: {
            enable: !!payload.sunday,
            endTime: payload.sunEndTime,
            startTime: payload.sunStartTime,
          },
          ['Sat']: {
            enable: !!payload.saturday,
            endTime: payload.satEndTime,
            startTime: payload.satStartTime,
          },
          ['Fri']: {
            enable: !!payload.friday,
            endTime: payload.friEndTime,
            startTime: payload.friStartTime,
          },
          ['Thu']: {
            enable: !!payload.thursday,
            endTime: payload.thuEndTime,
            startTime: payload.thuStartTime,
          },
          ['Wed']: {
            enable: !!payload.wednesday,
            endTime: payload.wedEndTime,
            startTime: payload.wedStartTime,
          },
          ['Tue']: {
            enable: !!payload.tuesday,
            endTime: payload.tueEndTime,
            startTime: payload.tueStartTime,
          },
          ['Mon']: {
            enable: !!payload.monday,
            endTime: payload.monEndTime,
            startTime: payload.monStartTime,
          },
        };
        state.scheduleBooking = _scheduleBooking;
      })
      .addCase(actions.category.success, (state, { payload }) => {
        state.categories = payload;
        const services: IServiceUI[] = [];
        state.categories.map(cate => {
          cate.services.map(s => {
            services.push({
              ...s,
              cateId: cate.id,
            });
          });
        });
        state.allServices = services;
      })
      .addCase(actions.staffAvailable.success, (state, { payload }) => {
        state.staffsAvailable = payload || [];
        state.enableCalendarPaging = state.staffsAvailable.length > STAFF_CALENDAR_PAGE_SIZE;
      })
      .addCase(actions.blockHourHistories.success, (state, { payload }) => {
        state.blockHourHistories = payload || { data: [], page: 1, pageSize: 20, total: 0 };
      })
      .addCase(actions.staff.success, (state, { payload }) => {
        state.staffs = payload;
        const staffsObject = {};
        payload.map((o: IStaffUI) => {
          set(staffsObject, o.id, o);
        });
        state.staffsObject = staffsObject;
      })
      .addCase(actions.customer.success, (state, { payload }) => {
        state.customers = payload;
      })
      .addCase(actions.afterCreateCustomer, (state, { payload }) => {
        state.customers.pop();
        state.customers.unshift(payload);
      })
      .addCase(actions.afterUpdateCustomer, (state, { payload }) => {
        state.customers = state.customers.map(o => {
          if (o.id?.toString() === payload?.id?.toString()) {
            return ({
              ...o,
              ...payload,
              id: o.id,
            });
          }
          return o;
        });
      })
      .addCase(actions.afterCreateService, (state, { payload }) => {
        state.allServices.unshift(payload);
        state.categories = state.categories.map(o => {
          if (o.id?.toString() === payload.cateId?.toString()) return ({
            ...o,
            services: [payload, ...o.services],
          });
          return o;
        });
      })
      ;
  },
});

const shopReducer = Slice.reducer;
export default shopReducer;
